<template>
  <ai-typo
    v-if="typeof amount === 'number' && symbol"
    class="AiAmount"
    :role="role"
    :class="{
      'AiAmount--strikeThrough': strikeThrough,
      'AiAmount--text-left': textAlign === 'left',
      'AiAmount--text-right': textAlign === 'right',
    }"
    :as="as"
    :variant="decimalTypo"
    :color="decimalColor || color">
    <ai-typo as="span" :variant="mainTypo" :color="mainColor || color">
      {{ main }}
    </ai-typo>
    <span>{{ decimal }} {{ symbol }}</span>
  </ai-typo>

  <div
    v-if="memberTag"
    class="AiAmount-memberTag"
    :class="{
      'AiAmount-memberTag--right': memberTag === 'right',
      'AiAmount-memberTag--left': memberTag === 'left',
      'AiAmount-memberTag--mobileInverse': memberTagMobileInverse,
    }">
    <ai-typo as="span" variant="overline-02">
      {{ $t('booking.memberTag') }}
    </ai-typo>
  </div>
</template>

<script setup lang="ts">
import type { AiTypoVariant, TypoColor } from '../../atoms/AiTypo/types';
import { extractPrice } from '../../helpers/extractPrice';

type Props = {
  amount?: number | null;
  symbol?: string | null;
  as?: keyof HTMLElementTagNameMap;
  color?: TypoColor;
  mainColor?: TypoColor;
  mainTypo?: AiTypoVariant;
  decimalColor?: TypoColor;
  decimalTypo?: AiTypoVariant;
  textAlign?: 'left' | 'right';
  memberTag?: 'left' | 'right';
  memberTagMobileInverse?: boolean;
  memberTagTopOffset?: string;
  strikeThrough?: boolean;
  role?: string;
};

const props = withDefaults(defineProps<Props>(), {
  amount: undefined,
  as: 'span',
  color: undefined,
  decimalColor: undefined,
  decimalTypo: 'paragraph-04-bold',
  mainColor: undefined,
  mainTypo: 'paragraph-02-bold',
  symbol: undefined,
  textAlign: undefined,
  memberTag: undefined,
  memberTagMobileInverse: undefined,
  memberTagTopOffset: '10%',
  role: undefined,
});

const main = computed(() => extractPrice(props.amount, 'main') ?? 0);
const decimal = computed(() => extractPrice(props.amount, 'decimal'));
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/fonts';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';

.AiAmount {
  position: relative;
  white-space: pre-line;

  &--text-right {
    text-align: right;
  }

  &--text-left {
    text-align: left;
  }
}

.AiAmount-memberTag {
  position: absolute;
  top: v-bind(memberTagTopOffset);
  text-transform: uppercase;
  background-color: colors.$gold-100;
  padding: constants.$inner-02 constants.$inner-03;
  border-radius: constants.$border-radius-01;
  isolation: isolate;
  @include fonts.overline-02;

  &::before {
    z-index: -1;
    position: absolute;
    top: 2px;
    content: '';
    background-color: colors.$gold-100;
    height: 25px;
    width: 25px;
    border-radius: constants.$border-radius-01;
    transform: rotate(45deg);
  }

  &--left {
    right: calc(100% + constants.$inner-05);

    &::before {
      right: -6px;
    }

    @media (mq.$upto-medium) {
      &.AiAmount-memberTag--mobileInverse {
        right: unset;
        left: calc(100% + constants.$inner-05);

        &::before {
          left: -6px;
        }
      }
    }
  }

  &--right {
    left: calc(100% + constants.$inner-05);

    &::before {
      left: -6px;
    }

    @media (mq.$upto-medium) {
      &.AiAmount-memberTag--mobileInverse {
        left: unset;
        right: calc(100% + constants.$inner-05);

        &::before {
          right: -6px;
        }
      }
    }
  }
}

.AiAmount--strikeThrough {
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    height: 1px;
    background-color: colors.$stratos-900;
    left: 0;
    right: 0;
    top: 50%;
  }
}
</style>
